import type React from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as X } from 'app/assets/icons/icon-remove-filter.svg';
import { ReactComponent as Eye } from 'app/assets/icons/show-password-icon.svg';
import imageDefault from 'app/assets/images/image-default.png';

import { COLOR } from '../Typography/core/enum';

const ShowImage = styled.div`
  min-width: 56px;
  min-height: 56px;
  width: 56px;
  height: 56px;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 5px;
  position: relative;
  cursor: pointer;
`;

const PreviewOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  color: ${COLOR.WHITE};
  font-size: 10px;
  font-weight: 500;
  gap: 2px;
  padding: 4px;
  box-sizing: border-box;

  ${ShowImage}:hover & {
    opacity: 1;
  }

  svg {
    margin-bottom: 2px;
  }

  svg path {
    stroke: ${COLOR.WHITE};
  }

  span {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 100%;
    text-align: center;
    line-height: 1;
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;

  &[data-visible='true'] {
    opacity: 1;
  }
`;

const ModalImage = styled.img`
  max-width: 100vw;
  max-height: 100vh;
  object-fit: contain;
  transform: scale(0.9);
  opacity: 0;
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;

  ${Modal}[data-visible="true"] & {
    transform: scale(1);
    opacity: 1;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transform: scale(0.9);
  opacity: 0;
  transition: transform 0.3s ease-out, opacity 0.3s ease-out, background 0.2s ease;

  ${Modal}[data-visible="true"] & {
    transform: scale(1);
    opacity: 1;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Figure = styled.figure`
  margin: 0;
  width: 56px;
  height: 56px;
  border-radius: 5px;
  background: rgb(242, 244, 247);
  position: relative;
`;

const ImageDefault = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-45%, -50%);
`;

interface Props {
  image: string;
  styleMain?: React.CSSProperties;
  isPreview?: boolean;
}

const ShowImageDefault: React.FC<Props> = ({ image, styleMain, isPreview }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleImageClick = () => {
    if (isPreview && image) {
      setIsModalOpen(true);
      setTimeout(() => setIsModalVisible(true), 50);
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setTimeout(() => setIsModalOpen(false), 300);
  };

  const handleModalClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      handleCloseModal();
    }
  };

  if (!image) {
    const fallbackSize = {
      width: ((styleMain?.width as number) || 56) - 6,
      height: ((styleMain?.height as number) || 56) - 6,
    };

    return (
      <Figure style={{ ...styleMain }}>
        <ImageDefault
          src={imageDefault}
          alt="Default"
          style={{ width: fallbackSize.width, height: fallbackSize.height }}
        />
      </Figure>
    );
  }

  return (
    <>
      <ShowImage
        style={{
          backgroundImage: `url(${image})`,
          cursor: isPreview ? 'pointer' : 'default',
          ...styleMain,
        }}
        onClick={handleImageClick}
      >
        {isPreview && (
          <PreviewOverlay>
            <Eye className="w-15px h-15px" />
            <span>Preview</span>
          </PreviewOverlay>
        )}
      </ShowImage>

      {isPreview && (
        <Modal
          isOpen={isModalOpen}
          data-visible={isModalVisible.toString()}
          onClick={handleModalClick}
        >
          <CloseButton onClick={handleCloseModal}>
            <X className="w-15px h-15px" />
          </CloseButton>
          <ModalImage src={image} alt="Preview" />
        </Modal>
      )}
    </>
  );
};

export default ShowImageDefault;
